/**
 * Dutch message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: informal je (locked in l10n/locales.md).
 *
 * Platform terms follow Dutch UI conventions (Thema for theme, gesprek
 * for conversation, koppelen for pair, wissen for dismiss, ontvanger
 * for a receiving surface); "Glance" is the untranslated feature brand
 * name (glossary D1) and "clip"/"clips" is a locked loanword (D2).
 *
 * Plural notes: Dutch is a {one, other} CLDR locale, and unlike German
 * its count nouns do inflect (teken → tekens, resultaat → resultaten),
 * so every count-bearing function selects through `selectPlural("nl", …)`.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Verbonden",
    statusReconnecting: "Opnieuw verbinden…",
    statusDisconnected: "Verbroken",
    openSettings: "Instellingen openen",
    pairedFallbackDevice: "je telefoon",
    publicModeToggleEnable: "Meldingsinhoud verbergen",
    publicModeToggleDisable: "Meldingsinhoud tonen",
  },

  notifications: {
    replyAriaLabel: "Antwoorden",
    replyPlaceholder: "Antwoorden…",
    replySend: "Verzenden",
    sent: "Verzonden",
    failedToSend: "Verzenden mislukt",
    unread: "Ongelezen",
    dismiss: "Wissen",
    notificationImageAlt: "Meldingsafbeelding",
  },

  conversations: {
    replyAriaLabel: "Antwoorden",
    replyPlaceholder: "Antwoorden…",
    replySend: "Verzenden",
    sent: "Verzonden",
    failedToSend: "Verzenden mislukt",
    dismiss: "Gesprek wissen",
    messagesListLabel: "Berichten in dit gesprek",
    deviceOwner: "Jij",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Geen ongelezen berichten"
        : `${count} ${selectPlural("nl", count, {
            one: "ongelezen bericht",
            other: "ongelezen berichten",
          })}`,
  },

  list: {
    today: "Vandaag",
    yesterday: "Gisteren",
    searchNotifications: "Meldingen zoeken",
    clearSearch: "Zoekopdracht wissen",
    clearAll: "Alles wissen",
    markAllRead: "Alles als gelezen markeren",
    groupCheckboxLabel: "Groeperen",
    noNotificationsTitle: "Nog geen meldingen",
    noNotificationsDesc:
      "Nieuwe meldingen die vanaf je telefoon worden gespiegeld, verschijnen hier.",
    noMatchesTitle: "Geen resultaten",
    notificationsList: "Meldingen",
    privacyAppList: "Meldingen per app",
    clear: "Wissen",
    clearMatches: (count: number): string =>
      `${count} ${selectPlural("nl", count, { one: "resultaat", other: "resultaten" })} wissen`,
    noMatchesDesc: (query: string): string => `Geen meldingen komen overeen met „${query}”.`,
    appNotificationsGroup: (appName: string): string => `Meldingen van ${appName}`,
    dismissAllFrom: (appName: string): string => `Alles van ${appName} wissen`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("nl", count, { one: "bericht", other: "berichten" })
          : kind === "calls"
            ? selectPlural("nl", count, { one: "oproep", other: "oproepen" })
            : selectPlural("nl", count, { one: "melding", other: "meldingen" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "wissen",
    kbdNavigate: "navigeren",
    kbdOrSeparator: "of",
  },

  settings: {
    back: "Terug naar meldingen",
    settings: "Instellingen",
    appearance: "Weergave",
    theme: "Thema",
    themeAuto: "Automatisch",
    themeLight: "Licht",
    themeDark: "Donker",
    language: "Taal",
    languageSystemDefault: "Systeemstandaard",
    notificationsSection: "Meldingen",
    osNotifications: "Systeemmeldingen",
    callNotifications: "Oproepmeldingen",
    privacySection: "Privacy",
    analyticsToggle: "Anonieme gebruiksstatistieken",
    analyticsToggleDescription: "Alleen aantallen en mijlpalen — nooit je meldingen.",
    connection: "Verbinding",
    status: "Status",
    statusConnected: "Verbonden",
    statusReconnecting: "Opnieuw verbinden…",
    statusDisconnected: "Verbroken",
    disconnectFromPhone: "Verbinding met telefoon verbreken",
    disconnectConfirm: "Weet je het zeker? Hiermee wordt je apparaat ontkoppeld.",
    yesDisconnect: "Ja, verbreken",
    cancel: "Annuleren",
    aboutAndSupport: "Over & ondersteuning",
    version: "Versie",
    encryption: "Versleuteling",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Hulp nodig? Mail __EMAIL__",
    disconnectFailed: "Verbinding verbreken mislukt — probeer het opnieuw",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Verbinding verbroken, maar het lokaal opschonen is mislukt: ${errorMsg}. Laad de pagina opnieuw.`,
    disconnectLocalCleanupFailed:
      "Verbinding verbroken, maar het lokaal opschonen is mislukt. Laad de pagina opnieuw.",
  },

  pairing: {
    enterCodeInstruction: "Voer deze code in de Reflecto-app in",
    pairedSuccessfully: "Koppeling geslaagd!",
    pairingFailed: "Koppeling mislukt",
    requestingPairingCode: "Koppelingscode aanvragen…",
    codeExpired: "Code verlopen",
    tryAgain: "Opnieuw proberen",
    failedToStartPairing: "Kon koppeling niet starten",
    needAppText: "Heb je de Android-app nodig? Mail __EMAIL__ voor toegang.",
  },

  dnd: {
    title: "Niet storen",
    description: "Pauzeer meldingen op dit apparaat voor het komende uur.",
    snooze1h: "1 uur pauzeren",
    snoozeActiveUntil: (formattedTime: string): string => `Actief tot ${formattedTime}`,
    cancelSnooze: "Annuleren",
  },

  connectors: {
    title: "Verbonden diensten",
    emptyBody:
      "Nog geen verbonden diensten. Verbind ChatGPT of Claude via hun connectorinstellingen en bevestig daarna de code op je telefoon.",
    scopeSend: "Kan meldingen sturen",
    added: (date: string): string => `Toegevoegd ${date}`,
    lastUsed: (relative: string): string => `Laatst gebruikt ${relative}`,
    neverUsed: "Nooit gebruikt",
    revoke: "Verbreken",
    revokeConfirm: (label: string): string =>
      `${label} verbreken? De toegang eindigt onmiddellijk.`,
    revokeAction: "Verbreken",
    revokeConfirmLabel: (label: string): string => `${label} verbreken`,
    cancel: "Annuleren",
    loadError: "Kon verbonden diensten niet laden. Probeer het opnieuw.",
    revokeError: "Kon de dienst niet verbreken. Probeer het opnieuw.",
    retry: "Opnieuw",
  },

  feedback: {
    title: "Feedback verzenden",
    close: "Sluiten",
    messageLabel: "Je bericht",
    messagePlaceholder:
      "Een bug gevonden, een idee, of voelt er iets niet goed? Laat het ons weten.",
    categoryLabel: "Categorie",
    categoryBug: "Bug",
    categoryIdea: "Idee",
    categoryOther: "Overig",
    emailLabel: "E-mail (optioneel, alleen als je een antwoord wilt)",
    emailPlaceholder: "jij@voorbeeld.nl",
    emailHelp: "We zetten je op geen enkele lijst.",
    disclosure:
      "Je bericht wordt naar de server van Reflecto verzonden en vastgelegd in onze interne issue-tracker. We voegen je app-versie en een bij de koppeling toegewezen apparaat-ID toe zodat we rapporten kunnen correleren. We zien je meldingen, contacten of andere persoonlijke gegevens niet. Je e-mailadres is optioneel — geef het alleen op als je een antwoord wilt. We zetten je op geen enkele lijst.",
    submit: "Feedback verzenden",
    sending: "Verzenden…",
    success: "Bedankt — wordt door een mens gelezen, meestal binnen 24 uur",
    error: "Verzenden mislukt. Probeer het opnieuw.",
    done: "Klaar",
    sendFeedback: "Feedback verzenden",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Sluiten",
    aiSummaryTag: "AI-samenvatting",
    errorAi: "Kon nu geen samenvatting maken.",
    retryCta: "Opnieuw proberen",
    downloadHeading: "AI-samenvattingen krijgen",
    downloadBody:
      "Kom bij met een AI-overzicht op het apparaat zelf van wat je hebt gemist. Draait volledig op je apparaat.",
    downloadCta: "Model downloaden",
    downloadPrivacyNote: "Eenmalige download. De server ziet je meldingen nooit.",
    downloadingHeading: "Model downloaden…",
    caughtUp: "Je bent helemaal bij — er is nu niets dat je aandacht vraagt.",
    needsYou: "Vereist aandacht",
    quiet: "Overig",
    scanning: (pending: number): string => `Samenvatten van ${pending}…`,
    jump: "Melding openen",
    affordanceReply: "Antwoorden",
    affordanceOpen: "Openen",
    affordanceCopyOtp: "Code kopiëren",
    buttonLabel: "Glance",
  },

  update: {
    title: "Update beschikbaar",
    description: "Er is een nieuwe versie van Reflecto klaar. Laad opnieuw om bij te werken.",
    reload: "Opnieuw laden",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `We hebben dit apparaat „${name}” genoemd.`,
    cardLooksGood: "Prima",
    cardEdit: "Bewerken",
    cardAriaLabel: "Apparaatnaam bevestigen",
    renameTitle: "Dit apparaat hernoemen",
    renameLabel: "Apparaatnaam",
    renameSave: "Opslaan",
    renameCancel: "Annuleren",
    renameFailed: "Hernoemen mislukt. Probeer het opnieuw.",
  },

  collision: {
    title: (name: string): string => `Je hebt al een „${name}”`,
    subtitle: "Hoe zullen we deze noemen?",
    chipWork: (name: string): string => `Werk ${name}`,
    chipHome: (name: string): string => `Thuis ${name}`,
    chipPersonal: (name: string): string => `Privé ${name}`,
    customLabel: "Aangepaste naam",
    save: "Opslaan",
    useAnyway: "Toch de oorspronkelijke naam gebruiken",
    saveFailed: "Kon de nieuwe naam niet opslaan. Probeer het opnieuw.",
    ariaLabel: "Dubbele apparaatnaam oplossen",
  },

  clipboard: {
    title: "Klembord",
    empty: "Clips van je andere apparaten verschijnen hier. Tik om te kopiëren.",
    from: (label: string): string => `van ${label}`,
    copy: "Naar klembord kopiëren",
    copied: "Gekopieerd",
    reveal: "Tonen",
    hide: "Verbergen",
    maskedHint: "Inhoud verborgen — druk op Tonen om weer te geven",
    sensitiveChip: "Gevoelig",
    cloudChip: "via je API-token",
    sendPlaceholder: "Plak tekst om te verzenden…",
    sendCta: "Verzenden",
    sendSensitiveToggle: "Als gevoelig markeren",
    sendSuccess: (n: number): string =>
      `Verzonden naar ${n} ${selectPlural("nl", n, { one: "apparaat", other: "apparaten" })}.`,
    sendPartial: (n: number, total: number): string =>
      `${n}/${total} ${selectPlural("nl", total, { one: "apparaat", other: "apparaten" })} bereikt.`,
    sendNoDevices: "Geen gekoppelde apparaten om naar te verzenden.",
    sendFailed: "Verzenden mislukt. Probeer het opnieuw.",
    sendEmpty: "Niets te verzenden.",
    dockExpand: "Klembordgeschiedenis tonen",
    dockCollapse: "Samenvouwen",
    dockSendTitle: "Naar je apparaten verzenden",
    dockFromClipboard: (chars: number): string =>
      `Van klembord · ${chars} ${selectPlural("nl", chars, {
        one: "teken",
        other: "tekens",
      })}`,
    dockSend: (n: number): string => `Verzenden naar ${n}`,
    dockCancel: "Annuleren",
    dockRecentHeading: "Recent",
    dockEmptyClipboard: "Je klembord is leeg.",
    shareTitle: "Naar je apparaten verzenden",
    shareReview:
      "Controleer wat je verzendt. Het gaat end-to-end-versleuteld naar je gekoppelde apparaten.",
    shareSend: "Verzenden",
    shareCancel: "Annuleren",
    shareNotPaired: "Koppel eerst een apparaat om gedeelde tekst te verzenden.",
    shareOpenApp: "Reflecto openen",
  },
  install: {
    title: "Reflecto installeren",
    body: "Voeg toe aan je startscherm voor snellere toegang.",
    cta: "Installeren",
    dismissAria: "Installatieprompt sluiten",
  },
  common: {
    loading: "Laden",
  },
  errors: {
    notPaired: "Niet gekoppeld",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} op ${os}`,
    webFallback: "Webbrowser",
  },

  palette: {
    placeholder: "Typ een opdracht…",
    noResults: "Geen overeenkomende opdrachten",
    ariaLabel: "Opdrachtenpalet",
    clearAll: "Alle meldingen wissen",
    glance: "Glance-samenvatting",
    cancelSnooze: "Pauze annuleren",
    copyClip: "Laatst ontvangen clip kopiëren",
  },
} satisfies MessagesShape;
